import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams } from 'react-router-dom';
import posthog from './analytics';
import { Toaster } from 'sonner';
import { AuthProvider, ModalProvider, useAuthContext, useModal } from './contexts';
import { useEmailVerification } from './hooks/useEmailVerification';
import { trackSitePageViewed, trackSiteVisitStarted } from './analyticsEvents';
import Layout from './components/Layout/Layout';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import AIMatchModal from './components/Modal/AIMatchModal';
import LoginModal from './components/Modal/LoginModal';
import TutorRegistrationModal from './components/Registration/TutorRegistrationModal';
import ParentRegistrationModal from './components/Registration/ParentRegistrationModal';
import MusicParentRegistrationModal from './components/Registration/MusicParentRegistrationModal';
import MusicTutorRegistrationModal from './components/Registration/MusicTutorRegistrationModal';
import WhatsAppButton from './components/WhatsAppButton';
import DebugReportOverlay from './debug/DebugReportOverlay';
import { pushBreadcrumb } from '@/debug/debugStore';
import LandingPage from './pages/LandingPage';
import AllTutorsPage from './pages/AllTutorsPage';
import AllCasesPage from './pages/AllCasesPage';
import CaseDetailPage from './pages/CaseDetailPage';
import TutorProfilePage from './pages/TutorProfilePage';
import AboutUsPage from './pages/AboutUsPage';
import HelpCenterPage from './pages/HelpCenterPage';
import TuitionFeeReferencePage from './pages/TuitionFeeReferencePage';
import HowItWorksPage from './pages/HowItWorksPage';
import MyCasesPage from './pages/MyCasesPage';
import MyApplicationsPage from './pages/MyApplicationsPage';
import UploadDocumentationPage from './pages/UploadDocumentationPage';
import EditCasePage from './pages/EditCasePage';
import EditTutorProfilePage from './pages/EditTutorProfilePage';
import EditParentProfilePage from './pages/EditParentProfilePage';
import MyLikedTutorsPage from './pages/MyLikedTutorsPage';
import TermsOfServiceCustomersPage from './pages/TermsOfServiceCustomersPage';
import TermsOfServiceTutorsPage from './pages/TermsOfServiceTutorsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TeachPage from './pages/TeachPage';
import MusicRegistrationFlowPage from './pages/MusicRegistrationFlowPage';
import MusicRegistrationCompletePage from './pages/MusicRegistrationCompletePage';
import NotFoundPage from './pages/NotFoundPage';
import { I18nProvider } from './i18n';
import { Navigate } from './i18n/navigation';
import { getLocaleRedirectPath, isSupportedLocale } from './i18n/routing';
import './index.css';

const PlaygroundPage = import.meta.env.DEV
  ? lazy(() => import('./pages/PlaygroundPage'))
  : null;

const DevPanelProvider = import.meta.env.DEV
  ? lazy(() => import('./dev/DevPanelContext').then((m) => ({ default: m.DevPanelProvider })))
  : null;

const DevPanel = import.meta.env.DEV
  ? lazy(() => import('./dev/DevPanel').then((m) => ({ default: m.DevPanel })))
  : null;

/**
 * Modal Renderer Component
 * Handles rendering of all modals based on ModalContext state
 */
function ModalRenderer() {
  const { pathname } = useLocation();
  const {
    showAIMatchModal,
    closeAIMatchModal,
    showLoginModal,
    closeLoginModal,
    showTutorRegistration,
    closeTutorRegistration,
    tutorRegistrationOptions,
    showParentRegistration,
    closeParentRegistration,
    openTutorRegistration,
    openParentRegistration,
    showMusicTutorRegistration,
    closeMusicTutorRegistration,
    showMusicParentRegistration,
    closeMusicParentRegistration,
  } = useModal();

  const handleSelectTutor = () => {
    closeAIMatchModal();
    openTutorRegistration();
  };

  const handleSelectParent = () => {
    closeAIMatchModal();
    openParentRegistration();
  };

  return (
    <div className={pathname.includes('/music/register/') || pathname.endsWith('/music-register/completion') ? 'theme-music' : 'theme-academic'}>
      <AIMatchModal
        isOpen={showAIMatchModal}
        onClose={closeAIMatchModal}
        onSelectTutor={handleSelectTutor}
        onSelectParent={handleSelectParent}
      />
      <LoginModal isOpen={showLoginModal} onClose={closeLoginModal} />
      <TutorRegistrationModal
        isOpen={showTutorRegistration}
        onClose={closeTutorRegistration}
        options={tutorRegistrationOptions}
      />
      <ParentRegistrationModal
        isOpen={showParentRegistration}
        onClose={closeParentRegistration}
      />
      <MusicTutorRegistrationModal isOpen={showMusicTutorRegistration} onClose={closeMusicTutorRegistration} />
      <MusicParentRegistrationModal isOpen={showMusicParentRegistration} onClose={closeMusicParentRegistration} />
    </div>
  );
}

function PageviewTracker() {
  const location = useLocation();
  const { backendUser } = useAuthContext();
  const previousPathRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}`;
    trackSiteVisitStarted(
      backendUser
        ? { userId: backendUser.userId, role: backendUser.currentRole }
        : null
    );
    trackSitePageViewed(previousPathRef.current);
    posthog.capture('$pageview');
    pushBreadcrumb({ category: 'navigation', message: `Navigate to ${location.pathname}` });
    previousPathRef.current = currentPath;
  }, [backendUser, location.pathname, location.search]);
  return null;
}

function AppShell() {
  useEmailVerification();

  return (
    <AuthProvider>
      <ModalProvider>
        <ScrollToTop />
        <PageviewTracker />
        <Layout>
          <Routes>
            {/* Public Routes */}
            <Route index element={<LandingPage />} />
            <Route path="tutors" element={<AllTutorsPage />} />
            <Route path="tutor/:id" element={<TutorProfilePage />} />
            <Route path="cases" element={<AllCasesPage />} />
            <Route path="cases/:id" element={<CaseDetailPage />} />
            <Route path="about-us" element={<AboutUsPage />} />
            <Route path="help-center" element={<HelpCenterPage />} />
            <Route path="tuition-fee" element={<TuitionFeeReferencePage />} />
            <Route path="how-it-works" element={<HowItWorksPage />} />
            <Route path="teach" element={<TeachPage />} />
            <Route path="music/register/*" element={<MusicRegistrationFlowPage />} />
            <Route path="music-register/completion" element={<MusicRegistrationCompletePage />} />
            <Route path="terms-customers" element={<TermsOfServiceCustomersPage />} />
            <Route path="terms-tutors" element={<TermsOfServiceTutorsPage />} />
            <Route path="privacy-policy" element={<PrivacyPolicyPage />} />

            {/* Protected Routes */}
            <Route
              path="parent/my-cases"
              element={
                <ProtectedRoute requiredRoles={['parent']}>
                  <MyCasesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="parent/my-liked-tutors"
              element={
                <ProtectedRoute requiredRoles={['parent']}>
                  <MyLikedTutorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="parent/edit-case/:caseId"
              element={
                <ProtectedRoute requiredRoles={['parent']}>
                  <EditCasePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="parent/edit-profile"
              element={
                <ProtectedRoute requiredRoles={['parent']}>
                  <EditParentProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="tutor/edit-profile"
              element={
                <ProtectedRoute requiredRoles={['tutor']}>
                  <EditTutorProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="tutor/my-applications"
              element={
                <ProtectedRoute requiredRoles={['tutor']}>
                  <MyApplicationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="upload-documentation"
              element={
                <ProtectedRoute>
                  <UploadDocumentationPage />
                </ProtectedRoute>
              }
            />

            {/* DEV-only: UI component playground */}
            {import.meta.env.DEV && PlaygroundPage && (
              <Route
                path="playground"
                element={
                  <Suspense fallback={null}>
                    <PlaygroundPage />
                  </Suspense>
                }
              />
            )}

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>

        {/* Global Toast Notifications */}
        <Toaster
          position="top-left"
          richColors
          closeButton
          expand={false}
          visibleToasts={3}
        />

        {/* Modal Renderer */}
        <ModalRenderer />

        {/* DEV Debug Panel */}
        {import.meta.env.DEV && DevPanelProvider && DevPanel && (
          <Suspense fallback={null}>
            <DevPanelProvider>
              <DevPanel />
            </DevPanelProvider>
          </Suspense>
        )}

        {/* WhatsApp Floating Button */}
        <WhatsAppButton phoneNumber="601112049396" />

        {/* Hidden debug report overlay — triggered by Ctrl+Shift+D or 7 taps on footer copyright */}
        <DebugReportOverlay />
      </ModalProvider>
    </AuthProvider>
  );
}

function LocalizedApp() {
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();

  if (!isSupportedLocale(locale)) {
    return (
      <Navigate
        to={getLocaleRedirectPath(location.pathname, location.search, location.hash)}
        replace
      />
    );
  }

  return (
    <I18nProvider>
      <AppShell />
    </I18nProvider>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/:locale/*" element={<LocalizedApp />} />
        <Route path="*" element={<LocalizedApp />} />
      </Routes>
    </Router>
  );
}

export default App;
